import { useState, useEffect } from 'react';
import { facilitatorAPI } from '../lib/api';
import { Button } from './ui/button';
import { CheckCircle2, Circle, ArrowRight, Send, Sparkles } from 'lucide-react';

const OnboardingChecklist = ({ profile, onDismiss, onGoToSection, onSubmit }) => {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (profile) {
      facilitatorAPI.getMyDocuments().then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
    } else {
      setDocs([]);
    }
  }, [profile]);

  const s1 = !!(profile && profile.full_name && profile.bio);
  const s2 = !!(profile && ((profile.years_experience && profile.years_experience > 0) || (profile.camp_roles && profile.camp_roles.length > 0)));
  const s3 = docs.length > 0;
  const coreDone = s1 && s2 && s3;

  const steps = [
    { key: 'basic', n: 1, title: 'Add your basic info', desc: 'Name, bio and profile photo', done: s1, section: 'basic' },
    { key: 'experience', n: 2, title: 'Set your experience and availability', desc: 'Camp roles, years of experience, availability', done: s2, section: 'experience' },
    { key: 'documents', n: 3, title: 'Upload your verification documents', desc: 'ID, police clearance and qualifications', done: s3, section: 'documents' },
  ];

  return (
    <div className="bg-card rounded-2xl border border-border p-6 mb-6 relative overflow-hidden" data-testid="onboarding-checklist">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-primary/5" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-2xl">Welcome to Campia — let's set up your profile</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Complete these steps to get listed and submitted for verification.</p>

        <ol className="space-y-3">
          {steps.map((step) => (
            <li
              key={step.key}
              className={`flex items-center gap-3 p-3 rounded-xl border ${step.done ? 'bg-green-50 border-green-200' : 'bg-muted/40 border-border'}`}
              data-testid={`onboarding-step-${step.key}`}
            >
              {step.done
                ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" data-testid={`onboarding-step-${step.key}-done`} />
                : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">Step {step.n}: {step.title}</p>
                <p className="text-xs text-muted-foreground">{step.desc}</p>
              </div>
              {!step.done && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full shrink-0"
                  onClick={() => onGoToSection(step.section)}
                  data-testid={`onboarding-goto-${step.key}`}
                >
                  Go to <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </li>
          ))}

          {/* Step 4: submit for review */}
          <li
            className={`flex items-center gap-3 p-3 rounded-xl border ${coreDone ? 'bg-primary/5 border-primary/30' : 'bg-muted/40 border-border'}`}
            data-testid="onboarding-step-submit"
          >
            <Circle className="w-5 h-5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Step 4: Submit for review</p>
              <p className="text-xs text-muted-foreground">Send your profile to our team for verification</p>
            </div>
            <Button
              type="button"
              size="sm"
              className="rounded-full shrink-0 bg-forest hover:bg-forest/90 text-white"
              disabled={!coreDone}
              onClick={onSubmit}
              data-testid="onboarding-submit-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" /> Submit for review
            </Button>
          </li>
        </ol>

        <div className="mt-4">
          <button
            type="button"
            onClick={onDismiss}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
            data-testid="onboarding-dismiss-btn"
          >
            I'll finish this later
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingChecklist;
